import React, { useState, useRef, useEffect, type FormEvent } from 'react';
import type { AppLockInstance } from '@ascend/applock';
import { Lock } from 'lucide-react';

export interface LockMenuProps {
  lock: AppLockInstance;
  /** Open the menu on mount. Used when a parent trigger (e.g. an overflow
   * menu) summons the lock management UI. */
  defaultOpen?: boolean;
  /** Notified whenever the menu closes itself (outside click / cancel), so a
   * controlling parent can unmount it. No-op when omitted (legacy usage). */
  onClose?: () => void;
}

type MenuView = 'closed' | 'main' | 'change' | 'remove';

/**
 * Compact header control for an enabled app lock. Opens a dropdown with
 * Lock now / Change password / Remove password actions. All `lock.*` calls
 * are guarded (deferred-encryption pattern — lock is non-null here but we
 * stay defensive).
 */
export function LockMenu({
  lock,
  defaultOpen = false,
  onClose,
}: LockMenuProps): React.ReactElement {
  const [view, setView] = useState<MenuView>(defaultOpen ? 'main' : 'closed');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    setView('closed');
    setCurrent('');
    setNext('');
    setError(null);
    setMessage(null);
    setBusy(false);
    onClose?.();
  };

  // Close the dropdown on outside click
  useEffect(() => {
    if (view === 'closed') return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        reset();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [view]);

  const handleLockNow = () => {
    if (!lock) return;
    lock.lock('manual');
    reset();
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!lock || busy) return;
    if (next.length < 4) {
      setError('New password must be at least 4 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await lock.changePassword(current, next);
      setMessage('Password changed.');
      setCurrent('');
      setNext('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change password.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemovePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!lock || busy) return;
    setBusy(true);
    setError(null);
    try {
      await lock.removePassword(current);
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove password.');
      setBusy(false);
    }
  };

  return (
    <div className="applock-menu" ref={wrapRef}>
      <button
        type="button"
        className="applock-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={view !== 'closed'}
        aria-label="App Lock menu"
        title="App Lock"
        onClick={() => (view === 'closed' ? setView('main') : reset())}
      >
        <Lock size={18} aria-hidden="true" />
      </button>

      {view === 'main' && (
        <div className="applock-menu__dropdown" role="menu">
          <button type="button" className="applock-menu__item" role="menuitem" onClick={handleLockNow}>
            Lock now
          </button>
          <button
            type="button"
            className="applock-menu__item"
            role="menuitem"
            onClick={() => {
              setError(null);
              setMessage(null);
              setView('change');
            }}
          >
            Change password
          </button>
          <button
            type="button"
            className="applock-menu__item"
            role="menuitem"
            onClick={() => {
              setError(null);
              setMessage(null);
              setView('remove');
            }}
          >
            Remove password
          </button>
        </div>
      )}

      {view === 'change' && (
        <form
          className="applock-menu__panel"
          role="dialog"
          aria-label="Change password"
          onSubmit={handleChangePassword}
        >
          <div className="input-group">
            <label htmlFor="applock-change-current" className="input-group__label">
              Current password
            </label>
            <input
              id="applock-change-current"
              className="input-group__input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="input-group">
            <label htmlFor="applock-change-next" className="input-group__label">
              New password
            </label>
            <input
              id="applock-change-next"
              className="input-group__input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </div>
          {error && (
            <p className="applock-menu__error" role="alert">
              {error}
            </p>
          )}
          {message && (
            <p className="applock-menu__message" role="status">
              {message}
            </p>
          )}
          <div className="applock-menu__panel-actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={reset} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {view === 'remove' && (
        <form
          className="applock-menu__panel"
          role="dialog"
          aria-label="Remove password"
          onSubmit={handleRemovePassword}
        >
          <p className="applock-menu__text">
            Removing the password disables the app lock. Enter your current password to confirm.
          </p>
          <div className="input-group">
            <label htmlFor="applock-remove-current" className="input-group__label">
              Current password
            </label>
            <input
              id="applock-remove-current"
              className="input-group__input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          {error && (
            <p className="applock-menu__error" role="alert">
              {error}
            </p>
          )}
          <div className="applock-menu__panel-actions">
            <button type="submit" className="btn btn--danger btn--sm" disabled={busy}>
              {busy ? 'Removing…' : 'Remove'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={reset} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
